import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Skeleton } from '@mui/material';
import Header from '../../../components/common/Header';
import Footer from '../../../components/common/Footer';
import Loading from '../../../components/common/Loading';
import PawUI from '../../../components/ui/PawUI';
import { isMobile } from 'react-device-detect';
import { Request } from '../../../config/requests';
import { getToken } from '../../../config/token';
import { showToast } from '../../../config/helpers';

/**
 * Manual bank payments.
 *
 * Two views in one page: the upload history, and one upload's lines. The page never parses
 * or allocates anything itself — it hands the file to the backend and then reads back what
 * the azure function wrote.
 *
 * An upload only opens once its run reports `processed` or `failed`. Its lines are stored
 * before allocation starts, so opening a running upload would show a page of `pending` rows
 * that are about to change under the reader.
 */

const STATUS_COLORS = {
    allocated: '#2e7d32',
    allocated_partial: '#ed6c02',
    no_unpaid_installments: '#6b6b6b',
    failed: '#c62828',
    duplicate: '#6b6b6b',
    pending: '#6b6b6b',
    processing: '#ed6c02',
    processed: '#2e7d32',
};

/**
 * Rows per page in the drill-down.
 *
 * A manual upload is usually a handful of lines, but a bank export is a whole month of
 * them and nothing stops someone uploading one — paging here costs nothing and keeps that
 * case from rendering a thousand-row table. The rows are already all in memory, so this is
 * a slice, not another request.
 */
const ROWS_PER_PAGE = 25;

/** Page a list in memory. Both tables hold every row already, so this is a slice. */
function paginate(items, page) {
    const pageCount = Math.max(1, Math.ceil(items.length / ROWS_PER_PAGE));
    const current = Math.min(Math.max(page, 1), pageCount);
    return {
        pageCount,
        page: current,
        items: items.slice((current - 1) * ROWS_PER_PAGE, current * ROWS_PER_PAGE),
        from: (current - 1) * ROWS_PER_PAGE + 1,
        to: Math.min(current * ROWS_PER_PAGE, items.length),
        total: items.length,
    };
}

const CELL = { padding: '10px 12px', verticalAlign: 'top' };
const HEAD_CELL = { ...CELL, fontWeight: 600, whiteSpace: 'nowrap' };

const money = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) ? `${amount.toFixed(2)} €` : '—';
};

function StatusChip({ value }) {
    const { t } = useTranslation();
    if (!value) return <span>—</span>;
    return (
        <span
            style={{
                color: STATUS_COLORS[value] || '#6b6b6b',
                fontWeight: 600,
                whiteSpace: 'nowrap',
            }}
        >
            {t(`manualPaymentStatus_${value}`, value.replace(/_/gu, ' '))}
        </span>
    );
}

const FILTER_CONTROL = {
    height: '38px',
    padding: '0 10px',
    border: '1px solid #d9d9d9',
    borderRadius: '6px',
    fontSize: '14px',
    background: '#fff',
};

/** One labelled control in a filter bar. */
function Field({ label, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', fontWeight: 600 }}>
            {label}
            {children}
        </label>
    );
}

/**
 * A `<select>` over the values a column actually holds.
 *
 * Options come from the loaded rows rather than from the enum, so the list never offers a
 * filter that would return nothing.
 */
function ValueSelect({ value, onChange, values, anyLabel, labelFor }) {
    return (
        <select style={{ ...FILTER_CONTROL, minWidth: '170px' }} value={value} onChange={(e) => onChange(e.target.value)}>
            <option value="">{anyLabel}</option>
            {values.map((v) => (
                <option key={v} value={v}>{labelFor ? labelFor(v) : v}</option>
            ))}
        </select>
    );
}

function FilterBar({ children, onClear, showClear }) {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '12px', padding: '12px 0 16px' }}>
            {children}
            {showClear && (
                <button className="button_red" style={{ width: '110px', margin: 0 }} onClick={onClear}>
                    {t('manualPaymentsClearFilters')}
                </button>
            )}
        </Box>
    );
}

function Pager({ state, onChange }) {
    const { t } = useTranslation();
    if (state.total <= ROWS_PER_PAGE) return null;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', padding: '12px' }}>
            <span style={{ fontSize: '14px' }}>
                {`${state.from}\u2013${state.to} / ${state.total}`}
            </span>
            <button
                className="button_red"
                style={{ width: '90px', margin: 0 }}
                disabled={state.page <= 1}
                onClick={() => onChange(state.page - 1)}
            >
                {t('manualPaymentsPrev')}
            </button>
            <button
                className="button_red"
                style={{ width: '90px', margin: 0 }}
                disabled={state.page >= state.pageCount}
                onClick={() => onChange(state.page + 1)}
            >
                {t('manualPaymentsNext')}
            </button>
        </Box>
    );
}

function ManualPayments() {
    const { t } = useTranslation();
    const token = getToken();
    const fileInputRef = useRef(null);

    const [runs, setRuns] = useState([]);
    const [isLoadingRuns, setIsLoadingRuns] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    const [openRun, setOpenRun] = useState(null);
    const [openRows, setOpenRows] = useState([]);
    const [isLoadingRows, setIsLoadingRows] = useState(false);
    const [rowsPage, setRowsPage] = useState(1);
    const [runsPage, setRunsPage] = useState(1);

    const [runFilters, setRunFilters] = useState({ from: '', to: '', uploader: '', status: '' });
    const [rowFilters, setRowFilters] = useState({ status: '', remaining: '' });

    // Changing a filter while on page 3 of the old result would show an empty table.
    const setRunFilter = (key, value) => { setRunFilters((f) => ({ ...f, [key]: value })); setRunsPage(1); };
    const setRowFilter = (key, value) => { setRowFilters((f) => ({ ...f, [key]: value })); setRowsPage(1); };

    /**
     * `silent` refreshes the list without the skeleton.
     *
     * Coming back from an upload, the list is already in state and almost always
     * unchanged — blanking it out to fetch the same rows reads as the page reloading for
     * no reason. The cached list stays on screen and is replaced when the answer lands.
     */
    const loadRuns = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setIsLoadingRuns(true);
        try {
            const res = await Request({ method: 'get', route: 'api/manual_payments/runs', token });
            const body = await res.json();
            const next = Array.isArray(body?.data) ? body.data : [];
            setRuns(next);
            // A refreshed list can be shorter than the page we were sitting on.
            setRunsPage(1);
        } catch (e) {
            console.error('manual payments runs error:', e);
        } finally {
            if (!silent) setIsLoadingRuns(false);
        }
    }, [token]);

    useEffect(() => {
        loadRuns();
    }, [loadRuns]);

    const handleUpload = async () => {
        if (!selectedFile) return;
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            const res = await Request({
                method: 'post',
                route: 'api/manual_payments/upload',
                token,
                formData,
            });
            const body = await res.json();
            if (body?.message) showToast(body.message);

            setSelectedFile(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
            await loadRuns();
        } catch (e) {
            console.error('manual payments upload error:', e);
        } finally {
            setIsUploading(false);
        }
    };

    const handleOpenRun = async (run) => {
        setIsLoadingRows(true);
        setOpenRun(run);
        setRowsPage(1);
        try {
            const res = await Request({
                method: 'get',
                route: `api/manual_payments/runs/${encodeURIComponent(run.documentId)}`,
                token,
            });
            const body = await res.json();
            setOpenRows(Array.isArray(body?.data?.rows) ? body.data.rows : []);
            if (body?.data?.message) showToast(body.data.message);
        } catch (e) {
            console.error('manual payments rows error:', e);
            setOpenRows([]);
        } finally {
            setIsLoadingRows(false);
        }
    };

    const isOpenable = (run) => run?.run_status === 'processed' || run?.run_status === 'failed';

    const uploaders = useMemo(
        () => [...new Set(runs.map((r) => r.uploaded_by).filter(Boolean))].sort(),
        [runs]
    );
    const runStatuses = useMemo(
        () => [...new Set(runs.map((r) => r.run_status).filter(Boolean))].sort(),
        [runs]
    );
    const rowStatuses = useMemo(
        () => [...new Set(openRows.map((r) => r.payment_status).filter(Boolean))].sort(),
        [openRows]
    );

    /**
     * Filtering is in the browser: both tables already hold every row they can show, so a
     * round trip would only make the controls feel sluggish. The one caveat is the uploads
     * list, which the backend caps at the newest 100 — a date older than that is not here
     * to be filtered.
     */
    const filteredRuns = useMemo(() => runs.filter((run) => {
        const day = String(run.started_at || run.createdAt || '').slice(0, 10);
        if (runFilters.from && (!day || day < runFilters.from)) return false;
        if (runFilters.to && (!day || day > runFilters.to)) return false;
        if (runFilters.uploader && run.uploaded_by !== runFilters.uploader) return false;
        if (runFilters.status && run.run_status !== runFilters.status) return false;
        return true;
    }), [runs, runFilters]);

    const filteredRows = useMemo(() => openRows.filter((row) => {
        if (rowFilters.status && row.payment_status !== rowFilters.status) return false;
        if (rowFilters.remaining) {
            const left = Number(row.remaining_amount) || 0;
            if (rowFilters.remaining === 'with' && left <= 0) return false;
            if (rowFilters.remaining === 'without' && left > 0) return false;
        }
        return true;
    }), [openRows, rowFilters]);

    const runFiltersActive = Object.values(runFilters).some(Boolean);
    const rowFiltersActive = Object.values(rowFilters).some(Boolean);

    const runsView = paginate(filteredRuns, runsPage);

    const renderRuns = () => (
        // Positioned, so the table paints above the PawUI decorations behind it — those
        // are absolutely positioned, and a static block would sit under them.
        <div className="w-100" style={{ overflowX: 'auto', position: 'relative', zIndex: 1, background: '#fff' }}>
            <FilterBar
                showClear={runFiltersActive}
                onClear={() => { setRunFilters({ from: '', to: '', uploader: '', status: '' }); setRunsPage(1); }}
            >
                <Field label={t('manualPaymentsFrom')}>
                    <input
                        type="date"
                        style={FILTER_CONTROL}
                        value={runFilters.from}
                        max={runFilters.to || undefined}
                        onChange={(e) => setRunFilter('from', e.target.value)}
                    />
                </Field>
                <Field label={t('manualPaymentsTo')}>
                    <input
                        type="date"
                        style={FILTER_CONTROL}
                        value={runFilters.to}
                        min={runFilters.from || undefined}
                        onChange={(e) => setRunFilter('to', e.target.value)}
                    />
                </Field>
                <Field label={t('manualPaymentsUploadedBy')}>
                    <ValueSelect
                        value={runFilters.uploader}
                        onChange={(v) => setRunFilter('uploader', v)}
                        values={uploaders}
                        anyLabel={t('manualPaymentsAnyUploader')}
                    />
                </Field>
                <Field label={t('manualPaymentsRunStatus')}>
                    <ValueSelect
                        value={runFilters.status}
                        onChange={(v) => setRunFilter('status', v)}
                        values={runStatuses}
                        anyLabel={t('manualPaymentsAnyStatus')}
                        labelFor={(v) => t(`manualPaymentStatus_${v}`, v.replace(/_/gu, ' '))}
                    />
                </Field>
            </FilterBar>
            <table className="w-100" style={{ borderCollapse: 'collapse', minWidth: '980px' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <th style={HEAD_CELL}>{t('manualPaymentsFile')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsUploadedBy')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsUploadedAt')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsRunStatus')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsRows')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsAmountTotal')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsAmountAllocated')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsAmountRemaining')}</th>
                        <th style={HEAD_CELL} />
                    </tr>
                </thead>
                <tbody>
                    {runsView.total === 0 && (
                        <tr>
                            <td style={CELL} colSpan={9}>
                                {runFiltersActive ? t('manualPaymentsNoMatches') : t('manualPaymentsNoUploads')}
                            </td>
                        </tr>
                    )}
                    {runsView.items.map((run) => (
                        <tr key={run.documentId} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={CELL}>{run.file_name || run.documentId}</td>
                            <td style={CELL}>{run.uploaded_by || '—'}</td>
                            <td style={{ ...CELL, whiteSpace: 'nowrap' }}>
                                {run.started_at ? String(run.started_at).slice(0, 19).replace('T', ' ') : '—'}
                            </td>
                            <td style={CELL}><StatusChip value={run.run_status} /></td>
                            <td style={{ ...CELL, whiteSpace: 'nowrap' }}>
                                {`${run.rows_allocated || 0} / ${run.rows_total || 0}`}
                                {run.rows_partial ? `, ${run.rows_partial} ${t('manualPaymentsPartialShort')}` : ''}
                                {run.rows_failed ? `, ${run.rows_failed} ${t('manualPaymentsFailedShort')}` : ''}
                                {run.rows_duplicate ? `, ${run.rows_duplicate} ${t('manualPaymentsDuplicateShort')}` : ''}
                            </td>
                            <td style={CELL}>{money(run.amount_total)}</td>
                            <td style={CELL}>{money(run.amount_allocated)}</td>
                            <td style={CELL}>{money(run.amount_remaining)}</td>
                            <td style={CELL}>
                                <button
                                    className="button_red"
                                    style={{ width: '120px', margin: 0 }}
                                    disabled={!isOpenable(run)}
                                    onClick={() => handleOpenRun(run)}
                                >
                                    {t('manualPaymentsOpen')}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <Pager state={runsView} onChange={setRunsPage} />
        </div>
    );

    const rowsView = paginate(filteredRows, rowsPage);

    const renderRows = () => (
        <div className="w-100" style={{ overflowX: 'auto', position: 'relative', zIndex: 1, background: '#fff' }}>
            <FilterBar
                showClear={rowFiltersActive}
                onClear={() => { setRowFilters({ status: '', remaining: '' }); setRowsPage(1); }}
            >
                <Field label={t('manualPaymentsRowStatus')}>
                    <ValueSelect
                        value={rowFilters.status}
                        onChange={(v) => setRowFilter('status', v)}
                        values={rowStatuses}
                        anyLabel={t('manualPaymentsAnyStatus')}
                        labelFor={(v) => t(`manualPaymentStatus_${v}`, v.replace(/_/gu, ' '))}
                    />
                </Field>
                <Field label={t('manualPaymentsAmountRemaining')}>
                    <select
                        style={{ ...FILTER_CONTROL, minWidth: '170px' }}
                        value={rowFilters.remaining}
                        onChange={(e) => setRowFilter('remaining', e.target.value)}
                    >
                        <option value="">{t('manualPaymentsAnyRemaining')}</option>
                        <option value="with">{t('manualPaymentsWithRemaining')}</option>
                        <option value="without">{t('manualPaymentsWithoutRemaining')}</option>
                    </select>
                </Field>
            </FilterBar>
            {openRun?.error && (
                <div style={{ color: '#c62828', marginBottom: '12px' }}>{openRun.error}</div>
            )}
            <table className="w-100" style={{ borderCollapse: 'collapse', minWidth: '1100px' }}>
                <thead>
                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                        <th style={HEAD_CELL}>#</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsBookingDate')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsPayer')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsPolicyNo')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsAmount')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsAllocatedTo')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsAmountAllocated')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsAmountRemaining')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsRowStatus')}</th>
                        <th style={HEAD_CELL}>{t('manualPaymentsReason')}</th>
                    </tr>
                </thead>
                <tbody>
                    {rowsView.total === 0 && (
                        <tr>
                            <td style={CELL} colSpan={10}>
                                {rowFiltersActive ? t('manualPaymentsNoMatches') : t('manualPaymentsNoRows')}
                            </td>
                        </tr>
                    )}
                    {rowsView.items.map((row) => {
                        const invoices = Array.isArray(row.invoice_nos) ? row.invoice_nos : [];
                        return (
                            <tr key={row.documentId} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={CELL}>{row.row_number}</td>
                                <td style={{ ...CELL, whiteSpace: 'nowrap' }}>{row.booking_date || '—'}</td>
                                <td style={CELL}>{row.payer_name || '—'}</td>
                                <td style={{ ...CELL, whiteSpace: 'nowrap' }}>{row.policy_no}</td>
                                <td style={CELL}>{money(row.amount)}</td>
                                <td style={CELL}>
                                    {invoices.length === 0 ? '—' : invoices.map((i) => (
                                        <div key={i.invoice_no} style={{ whiteSpace: 'nowrap' }}>
                                            {`${i.invoice_no} — ${money(i.amount)}`}
                                        </div>
                                    ))}
                                </td>
                                <td style={CELL}>{money(row.allocated_amount)}</td>
                                <td style={CELL}>{money(row.remaining_amount)}</td>
                                <td style={CELL}><StatusChip value={row.payment_status} /></td>
                                <td style={{ ...CELL, minWidth: '260px' }}>{row.reason || '—'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <Pager state={rowsView} onChange={setRowsPage} />
        </div>
    );

    return (
        <div className="main-container SlowShow">
            <Header />
            <div className="page-content relative claims-content">
                <div className="claims" style={{ maxWidth: 'unset' }}>
                    {isMobile ? (
                        <>
                            <PawUI top="1vh" right="6vw" rotation="right" width="25vw" zIndex="0" />
                            <PawUI bottom="-23vh" right="-27vw" rotation="left" width="70vw" zIndex="0" />
                        </>
                    ) : (
                        <>
                            <PawUI top="20vh" right="1vw" rotation="right" width="350px" zIndex="0" />
                            <PawUI bottom="-10vh" left="5vw" rotation="left" width="150px" zIndex="0" />
                        </>
                    )}

                    <div className="w-100 my-3 d-flex justify-content-between align-items-center dark_red_title relative">
                        <div style={{ whiteSpace: 'nowrap' }}>
                            {openRun ? (openRun.file_name || openRun.documentId) : t('manualPayments')}
                        </div>
                        <Box sx={{ display: 'flex', columnGap: '20px' }}>
                            {openRun ? (
                                <button
                                    className="button_red"
                                    style={{ width: '220px', margin: 0 }}
                                    onClick={() => { setOpenRun(null); setOpenRows([]); loadRuns({ silent: true }); }}
                                >
                                    {t('manualPaymentsBackToUploads')}
                                </button>
                            ) : (
                                <>
                                    {/* The native file input renders as a wide, unstyled
                                        control that does not sit with the page's buttons.
                                        Hidden, and driven by one of them instead. */}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".csv,text/csv"
                                        onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                        style={{ display: 'none' }}
                                    />
                                    {selectedFile && (
                                        <span
                                            title={selectedFile.name}
                                            style={{
                                                alignSelf: 'center',
                                                maxWidth: '260px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '14px',
                                                fontWeight: 400,
                                            }}
                                        >
                                            {selectedFile.name}
                                        </span>
                                    )}
                                    <button
                                        className="button_red"
                                        style={{ width: '140px', margin: 0 }}
                                        disabled={isUploading}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        {t('manualPaymentsChooseFile')}
                                    </button>
                                    <button
                                        className="button_red"
                                        style={{ width: '140px', margin: 0 }}
                                        disabled={!selectedFile || isUploading}
                                        onClick={handleUpload}
                                    >
                                        {isUploading
                                            ? <Loading noText fast scroll={false} color={'#fff'} />
                                            : t('manualPaymentsUpload')}
                                    </button>
                                    <button
                                        className="button_red"
                                        style={{ width: '140px', margin: 0 }}
                                        disabled={isLoadingRuns}
                                        onClick={loadRuns}
                                    >
                                        {t('manualPaymentsRefresh')}
                                    </button>
                                </>
                            )}
                        </Box>
                    </div>

                    {(isLoadingRuns && !openRun) || isLoadingRows ? (
                        <Skeleton variant="rounded" width={'100%'} height={550} style={{ margin: '2vh 0' }} />
                    ) : (
                        openRun ? renderRows() : renderRuns()
                    )}
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default ManualPayments;
